import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectAlgoStartDate } from '@/store/orders/algoOrders/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT, getDateNow } from '@/utils/date.ts';

export const AlgoStartDate = () => {
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const startDate = useAppSelector((state) => selectAlgoStartDate(state, tileId));

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleIsNowChange = (isNow: boolean) => {
    track('Order action', 'Is now changed');
    validateAlgo({
      startDate: isNow
        ? { isNow: true, date: format(getDateNow(), 'yyyy-MM-dd'), time: format(getDateNow(), 'HH:mm:ss') }
        : { isNow: false, date: startDate?.date, time: startDate?.time },
    });
  };

  const handleDateChange = (date: string) => {
    track('Order action', 'Start date changed');
    if (isNotDefined(startDate) || startDate.isNow) {
      return null;
    }

    validateAlgo({
      startDate: {
        isNow: false,
        date,
        time: startDate?.time,
      },
    });
  };

  const handleTimeChange = (data: {
    time?: string;
    hour?: number;
    minute?: number;
    second?: number;
    period?: 'AM' | 'PM';
  }) => {
    track('Order action', 'Start time changed');
    if (isNotDefined(startDate) || startDate.isNow) {
      return null;
    }

    const hour = data.hour ?? 0;
    const getHours = data.period === 'PM' && hour < 12 ? hour + 12 : hour;
    const hours = isDefined(data.hour) ? getHours : 0;
    const minutes = isDefined(data.minute) ? data.minute : 0;
    const seconds = isDefined(data.second) ? data.second : 0;

    if (isDefined(startDate.date)) {
      const formattedHours = hours <= 9 ? `0${hours}` : hours;
      const formattedMinutes = minutes <= 9 ? `0${minutes}` : minutes;
      const formattedSeconds = seconds <= 9 ? `0${seconds}` : seconds;
      const newTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
      const date = format(startDate.date, 'yyyy-MM-dd');

      validateAlgo({
        startDate: {
          isNow: false,
          date: date,
          time: newTime,
        },
      });
    }
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const startDateDateError = errors?.startDate;

  return (
    <OrderRow labelId="order.fields.startDate.label" alignTop>
      <Checkbox
        data-e2e="isNow-checkbox"
        checked={startDate?.isNow}
        onCheckBoxChange={handleIsNowChange}
        label={formatMessage({ id: 'order.fields.startDate.now' })}
        size="sm"
      />

      {!startDate?.isNow && (
        <div
          className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
          data-e2e="start-date"
        >
          <OmsFieldErrorWrapper errors={startDateDateError}>
            <SgDatePicker
              size="sm"
              yearControls
              value={startDate?.date}
              placeholder="Start date"
              locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
              dateInputCultureInfo={getComputedLocale()}
              onChange={handleDateChange}
              onFocus={() => setIsModifyingField(true)}
              onBlurInput={() => setIsModifyingField(false)}
              showClearButton={false}
              validationState={isDefined(startDateDateError) ? 'invalid' : undefined}
              dateFormat={DATE_FORMAT_TEXT}
            />
          </OmsFieldErrorWrapper>
          <div data-e2e="startDate-time-input">
            <SgTimepicker
              size="sm"
              appendTo="body"
              withSeconds
              onTimeSelected={(e) => handleTimeChange(e.detail)}
              value={startDate?.time}
            />
          </div>
        </div>
      )}
    </OrderRow>
  );
};
